import { assertTypedData, compileTypedCalculations, compileTypedExpression, inspectTypedExpressionStructure, NativeExpressionError } from "./nativeExpression.mjs";
import { compileReportParameters, validatePresentationFilters } from "./nativeParameters.mjs";

function fail(code, message) { throw new NativeExpressionError(`NATIVE_EXPRESSION_${code}`, "$.layout", message); }
function plain(value) {
  return assertTypedData(value);
}
function formatType(target, type) {
  if ((["number", "money"].includes(target.format) && type.type !== "number") || (target.format === "date" && type.type !== "date")) fail("TYPE", "Typed presentation format must match the inferred result type.");
}
function descriptors(dataset) {
  plain(dataset);
  assertTypedData(dataset.fields, 16384);
  const fields = Object.create(null);
  dataset.fields.forEach(field => {
    plain(field);
    if (typeof field.path !== "string" || Object.prototype.hasOwnProperty.call(fields, field.path)) fail("REFERENCE", "Dataset field identities must be unique.");
    fields[field.path] = { type: field.type, nullable: field.nullable };
  });
  return fields;
}
export function compileTypedReportTargets(payload, tables, textTargets = []) {
  const parameters = compileReportParameters(payload.layout?.parameters);
  const filters = validatePresentationFilters(payload);
  const configuration = plain(payload.data);
  assertTypedData(configuration.datasets, 128);
  const datasets = new Map();
  const document = Object.create(null);
  let fieldCount = 0;
  for (const candidate of configuration.datasets) {
    plain(candidate);
    if (typeof candidate.id !== "string" || datasets.has(candidate.id)) fail("REFERENCE", "Dataset identities must be unique.");
    if (!["single", "collection"].includes(candidate.kind)) fail("TYPE", "Dataset cardinality must be explicit.");
    const fields = descriptors(candidate);
    fieldCount += Object.keys(fields).length;
    if (fieldCount > 16384) fail("LIMIT", "Dataset field contract limit exceeded.");
    datasets.set(candidate.id, { candidate, fields });
    if (candidate.kind === "single") for (const [path, descriptor] of Object.entries(fields)) document[`${candidate.id}.${path}`] = descriptor;
  }
  let nodes = 0;
  const tablePlans = new Map();
  const textPlans = new Map();
  const filterPlans = new Map();
  for (const table of tables) {
    const dataset = datasets.get(table.dataset);
    if (!dataset || dataset.candidate.kind !== "collection") fail("SCOPE", "Typed calculations require their declared collection dataset.");
    const declarations = Object.create(null);
    const used = new Set();
    for (const column of table.columns) {
      plain(column);
      if (typeof column.id !== "string" || used.has(column.id)) fail("REFERENCE", "Column identities must be explicit and unique.");
      used.add(column.id);
      if (column.expr !== undefined && column.calculation !== undefined) fail("MIXED_MODE", "Calculation aliases cannot overlap in a typed table.");
      if (column.expr?.typed !== undefined && (column.binding !== undefined || column.key !== undefined)) fail("MIXED_MODE", "Typed expressions cannot overlap binding or key carriers.");
      if (column.expr !== undefined || column.calculation !== undefined) declarations[column.id] = column.expr || column.calculation;
    }
    const plan = compileTypedCalculations(declarations, { fields: { detail: dataset.fields, document }, parameters });
    const compiledFilters = [];
    for (const filter of filters) {
      if (filter.tableId !== table.id) continue;
      const compiled = plan.compile(filter.when);
      if (compiled.type.type !== "boolean" || compiled.type.nullable) fail("TYPE", "Presentation filters must return nonnullable boolean.");
      nodes += inspectTypedExpressionStructure(filter.when).nodes;
      compiledFilters.push(compiled);
    }
    filterPlans.set(table.id, compiledFilters);
    for (const column of table.columns) if (column.expr?.typed !== undefined) formatType(column, plan.types[column.id]);
    if (tablePlans.has(table.id)) fail("REFERENCE", "Typed table identities must be unique.");
    tablePlans.set(table.id, plan);
    nodes += plan.nodes;
    if (nodes > 16384) fail("LIMIT", "Report expression definition-node limit exceeded.");
  }
  for (const { element, band } of textTargets) {
    if (element.type !== "text" || element.text !== undefined || element.binding !== undefined || element.calculation !== undefined) fail("TARGET", "A typed text value cannot overlap static text, bindings or calculation aliases.");
    const dataset = band.role === "detail" && band.dataset ? datasets.get(band.dataset) : undefined;
    if (band.dataset && band.role === "detail" && dataset?.candidate.kind !== "collection") fail("SCOPE", "Detail text requires a declared collection dataset.");
    const options = { fields: { document, ...(dataset ? { detail: dataset.fields } : {}) }, allowedScopes: dataset ? ["detail", "document"] : ["document"], parameters };
    const plan = compileTypedExpression(element.expr, options);
    formatType(element, plan.type);
    nodes += (function count(node) { return 1 + (node.args || []).reduce((sum, child) => sum + count(child), 0); })(plan.canonical.typed);
    if (nodes > 16384) fail("LIMIT", "Report expression definition-node limit exceeded.");
    if (textPlans.has(element.id)) fail("REFERENCE", "Typed text identities must be unique.");
    textPlans.set(element.id, plan);
  }
  return { tablePlans, textPlans, filterPlans, parameters };
}
export function validateTypedReportTables(payload, tables, textTargets = []) {
  compileTypedReportTargets(payload, tables, textTargets);
}
